import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { useEffect, useCallback, useState } from 'react';
import { cn } from './cn';

// ── Toolbar-Button ────────────────────────────────────────────────────────────

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center justify-center h-7 min-w-7 px-1.5 text-xs font-bold border transition-colors duration-100 cursor-pointer select-none',
        active
          ? 'bg-brand text-white border-brand'
          : 'bg-transparent text-ink-muted border-ink/10 hover:bg-ink/5 hover:text-ink hover:border-ink/20',
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
      )}
    >
      {children}
    </button>
  );
}

// ── HTML-Source-Icon (inline SVG, kein externer Icon-Import nötig) ─────────────

function HtmlSourceIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────────

interface ToolbarProps {
  editor: ReturnType<typeof useEditor>;
  disabled?: boolean;
  htmlMode: boolean;
  onToggleHtmlMode: () => void;
}

function Toolbar({ editor, disabled, htmlMode, onToggleHtmlMode }: ToolbarProps) {
  if (!editor) return null;

  const handleLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link-URL', previousUrl ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-ink/10 bg-paper">
      {/* WYSIWYG-Formatierungsbuttons — im HTML-Modus ausgeblendet */}
      {!htmlMode && (
        <>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            disabled={disabled}
            title="Fett (Strg+B)"
          >
            <strong>B</strong>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            disabled={disabled}
            title="Kursiv (Strg+I)"
          >
            <em>I</em>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            disabled={disabled}
            title="Unterstrichen (Strg+U)"
          >
            <span className="underline">U</span>
          </ToolbarButton>

          <span className="w-px h-5 bg-ink/10 mx-0.5" aria-hidden="true" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            disabled={disabled}
            title="Überschrift H2"
          >
            H2
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            disabled={disabled}
            title="Überschrift H3"
          >
            H3
          </ToolbarButton>

          <span className="w-px h-5 bg-ink/10 mx-0.5" aria-hidden="true" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            disabled={disabled}
            title="Aufzählungsliste"
          >
            — Liste
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            disabled={disabled}
            title="Nummerierte Liste"
          >
            1. Liste
          </ToolbarButton>

          <span className="w-px h-5 bg-ink/10 mx-0.5" aria-hidden="true" />

          <ToolbarButton
            onClick={handleLink}
            active={editor.isActive('link')}
            disabled={disabled}
            title="Link einfügen/entfernen"
          >
            Link
          </ToolbarButton>

          <span className="w-px h-5 bg-ink/10 mx-0.5" aria-hidden="true" />
        </>
      )}

      {/* HTML-Source-Toggle — immer sichtbar, rechts ausgerichtet */}
      <div className="ml-auto">
        <ToolbarButton
          onClick={onToggleHtmlMode}
          active={htmlMode}
          disabled={disabled}
          title={htmlMode ? 'WYSIWYG-Ansicht' : 'HTML-Quelltext anzeigen'}
        >
          <span className="flex items-center gap-1">
            <HtmlSourceIcon size={13} />
            <span className="text-[11px]">HTML</span>
          </span>
        </ToolbarButton>
      </div>
    </div>
  );
}

// ── RichTextEditor ────────────────────────────────────────────────────────────

/**
 * Props für den RichTextEditor.
 */
export interface RichTextEditorProps {
  /** Aktueller HTML-Inhalt */
  content: string;
  /** Callback, der bei jeder Änderung den neuen HTML-String liefert */
  onChange: (html: string) => void;
  /** Platzhaltertext wenn der Editor leer ist */
  placeholder?: string;
  /** Deaktiviert den Editor (readonly) */
  disabled?: boolean;
  /** Zusätzliche CSS-Klassen für den äußeren Container */
  className?: string;
  /** Minimale Höhe des Editorbereichs in Pixeln (Standard: 160) */
  minHeight?: number;
}

/**
 * WYSIWYG Rich-Text-Editor basierend auf TipTap mit HTML-Source-Toggle.
 * Gibt HTML-Strings zurück, die direkt in der Datenbank gespeichert werden können.
 *
 * Toolbar-Funktionen: Fett, Kursiv, Unterstrichen, H2, H3, Aufzählung, Nummerierung, Link.
 * HTML-Modus: Textarea mit Monospace-Font für direktes Bearbeiten des HTML-Quellcodes.
 * Design ist auf das Dashboard-Dark-Theme abgestimmt.
 *
 * @param props.content - Aktueller HTML-Inhalt (kontrollierte Komponente)
 * @param props.onChange - Wird bei jeder Änderung mit dem neuen HTML aufgerufen
 * @param props.placeholder - Platzhaltertext (optional)
 * @param props.disabled - Editor deaktivieren (optional)
 * @param props.className - Zusätzliche Klassen für den Container (optional)
 * @param props.minHeight - Minimale Höhe des Editierbereichs in px (Standard: 160)
 *
 * @example
 * ```tsx
 * <RichTextEditor
 *   content={body}
 *   onChange={(html) => setBody(html)}
 *   placeholder="Text eingeben…"
 * />
 * ```
 */
export function RichTextEditor({
  content,
  onChange,
  placeholder = 'Text eingeben…',
  disabled = false,
  className,
  minHeight = 160,
}: RichTextEditorProps) {
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState('');

  const handleUpdate = useCallback(
    ({ editor }: { editor: ReturnType<typeof useEditor> }) => {
      if (!editor) return;
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    [onChange],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Heading Level 1 deaktivieren — nur H2/H3 erlaubt
        heading: { levels: [2, 3] },
      }),
      Underline,
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
    ],
    content,
    editable: !disabled,
    onUpdate: handleUpdate,
  });

  // Externen Content-Reset (z.B. beim Öffnen eines anderen Blocks) synchronisieren
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const normalized = current === '<p></p>' ? '' : current;
    if (normalized !== content) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // disabled-State synchronisieren
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  /**
   * Beim Wechsel in den HTML-Modus: aktuellen TipTap-HTML-Inhalt in die Textarea übernehmen.
   * Beim Wechsel zurück in den WYSIWYG-Modus: Textarea-Inhalt in den Editor laden und nach oben propagieren.
   */
  function handleToggleHtmlMode() {
    if (!editor) return;

    if (!htmlMode) {
      // WYSIWYG → HTML: aktuellen Inhalt in Textarea-Draft kopieren
      const current = editor.getHTML();
      setHtmlDraft(current === '<p></p>' ? '' : current);
      setHtmlMode(true);
    } else {
      // HTML → WYSIWYG: Draft in Editor laden und onChange aufrufen
      editor.commands.setContent(htmlDraft);
      onChange(htmlDraft);
      setHtmlMode(false);
    }
  }

  return (
    <div
      className={cn(
        'border-2 border-ink/10 bg-card transition-colors duration-150',
        !disabled && 'focus-within:border-brand',
        disabled && 'opacity-60',
        className,
      )}
    >
      <Toolbar
        editor={editor}
        disabled={disabled}
        htmlMode={htmlMode}
        onToggleHtmlMode={handleToggleHtmlMode}
      />

      {htmlMode ? (
        /* HTML-Source-Modus: Monospace-Textarea */
        <textarea
          value={htmlDraft}
          onChange={(e) => setHtmlDraft(e.target.value)}
          disabled={disabled}
          spellCheck={false}
          aria-label="HTML-Quelltext"
          style={{ minHeight }}
          className={cn(
            'w-full px-3 py-2.5 font-mono text-xs text-ink bg-ink/5 resize-y outline-none leading-relaxed',
            'placeholder:text-ink-muted',
            disabled && 'cursor-not-allowed',
          )}
        />
      ) : (
        /* WYSIWYG-Modus: TipTap EditorContent */
        <EditorContent
          editor={editor}
          style={{ minHeight }}
          className={cn(
            '[&_.tiptap]:min-h-[inherit] [&_.tiptap]:px-3 [&_.tiptap]:py-2.5 [&_.tiptap]:text-sm [&_.tiptap]:text-ink [&_.tiptap]:leading-relaxed [&_.tiptap]:outline-none',
            '[&_.tiptap_h2]:text-base [&_.tiptap_h2]:font-black [&_.tiptap_h2]:uppercase [&_.tiptap_h2]:tracking-wide [&_.tiptap_h2]:text-ink [&_.tiptap_h2]:mt-3 [&_.tiptap_h2]:mb-1',
            '[&_.tiptap_h3]:text-sm [&_.tiptap_h3]:font-bold [&_.tiptap_h3]:text-ink [&_.tiptap_h3]:mt-2 [&_.tiptap_h3]:mb-1',
            '[&_.tiptap_ul]:list-disc [&_.tiptap_ul]:ml-5 [&_.tiptap_ul]:my-1',
            '[&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:ml-5 [&_.tiptap_ol]:my-1',
            '[&_.tiptap_li]:my-0.5',
            '[&_.tiptap_a]:text-brand [&_.tiptap_a]:underline [&_.tiptap_a]:underline-offset-2 [&_.tiptap_a]:hover:text-brand-dark',
            '[&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_p.is-editor-empty:first-child::before]:text-ink-muted [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0',
            '[&_.tiptap_p]:my-1',
          )}
        />
      )}
    </div>
  );
}
